import * as XLSX from 'xlsx';

// Export data to CSV format
export const exportToCSV = (data, filename = 'campaign-affiliate-stats') => {
  try {
    // Define the CSV headers based on the image format
    const headers = [
      // Campaign Master Data
      'Company Name',
      'Brand Name', 
      'Campaign Name',
      'Influencer Name (Affiliate Name)',
      // YouTube Performance
      'Videos',
      'Views',
      'Likes', 
      'Comments',
      'Engagements',
      // Instagram Performance
      'Reachs',
      'Likes',
      'Comments',
      'Engagements'
    ];

    // Prepare CSV content with section headers
    let csvContent = '';
    
    // Add section headers
    csvContent += 'Campaign Master Data,,,,YouTube Performance,,,,,Instagram Performance,,,,\n';
    csvContent += headers.join(',') + '\n';

    // Add data rows
    data.forEach(row => {
      const csvRow = [
        row.companyName || '',
        row.brandName || '',
        row.campaignName || '',
        row.influencerName || '',
        row.youtubeVideos || 0,
        row.youtubeViews || 0,
        row.youtubeLikes || 0,
        row.youtubeComments || 0,
        row.youtubeEngagements || 0,
        row.instagramReachs || 0,
        row.instagramLikes || 0,
        row.instagramComments || 0,
        row.instagramEngagements || 0
      ];
      
      // Escape commas and quotes in CSV
      const escapedRow = csvRow.map(field => {
        if (typeof field === 'string' && (field.includes(',') || field.includes('"'))) {
          return `"${field.replace(/"/g, '""')}"`;
        }
        return field;
      });
      
      csvContent += escapedRow.join(',') + '\n';
    });

    // Create and download CSV file
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    
    if (link.download !== undefined) {
      const url = URL.createObjectURL(blob);
      link.setAttribute('href', url);
      link.setAttribute('download', `${filename}.csv`);
      link.style.visibility = 'hidden';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    } else {
      // Fallback for older browsers
      window.open('data:text/csv;charset=utf-8,' + encodeURIComponent(csvContent));
    }

    return { success: true, message: 'CSV exported successfully' };
  } catch (error) {
    console.error('Error exporting to CSV:', error);
    return { success: false, message: 'Failed to export CSV', error: error.message };
  }
};

// Export data to Excel format
export const exportToExcel = (data, filename = 'campaign-affiliate-stats') => {
  try {
    // Define the Excel headers based on the image format
    const headers = [
      // Campaign Master Data
      'Company Name',
      'Brand Name', 
      'Campaign Name',
      'Influencer Name (Affiliate Name)',
      // YouTube Performance
      'Videos',
      'Views',
      'Likes', 
      'Comments',
      'Engagements',
      // Instagram Performance
      'Reachs',
      'Likes',
      'Comments',
      'Engagements'
    ];

    // Prepare worksheet data with section headers
    const worksheetData = [];
    
    // Add section header row
    worksheetData.push(['Campaign Master Data', '', '', '', 'YouTube Performance', '', '', '', '', 'Instagram Performance', '', '', '']);
    
    // Add column headers row
    worksheetData.push(headers);

    // Add data rows
    data.forEach(row => {
      const excelRow = [
        row.companyName || '',
        row.brandName || '',
        row.campaignName || '',
        row.influencerName || '',
        row.youtubeVideos || 0,
        row.youtubeViews || 0,
        row.youtubeLikes || 0,
        row.youtubeComments || 0,
        row.youtubeEngagements || 0,
        row.instagramReachs || 0,
        row.instagramLikes || 0,
        row.instagramComments || 0,
        row.instagramEngagements || 0
      ];
      
      worksheetData.push(excelRow);
    });

    // Create workbook and worksheet
    const workbook = XLSX.utils.book_new();
    const worksheet = XLSX.utils.aoa_to_sheet(worksheetData);

    // Set column widths for better readability
    const columnWidths = [
      { wch: 20 }, // Company Name
      { wch: 20 }, // Brand Name
      { wch: 25 }, // Campaign Name
      { wch: 25 }, // Influencer Name
      { wch: 10 }, // Videos
      { wch: 12 }, // Views
      { wch: 10 }, // Likes
      { wch: 12 }, // Comments
      { wch: 15 }, // Engagements
      { wch: 15 }, // Reachs
      { wch: 10 }, // Likes
      { wch: 12 }, // Comments
      { wch: 15 }  // Engagements
    ];
    worksheet['!cols'] = columnWidths;

    // Add cell merging for section headers
    worksheet['!merges'] = [
      // Campaign Master Data (spans 4 columns)
      { s: { r: 0, c: 0 }, e: { r: 0, c: 3 } },
      // YouTube Performance (spans 5 columns)
      { s: { r: 0, c: 4 }, e: { r: 0, c: 8 } },
      // Instagram Performance (spans 4 columns)
      { s: { r: 0, c: 9 }, e: { r: 0, c: 12 } }
    ];

    // Style the section header row (first row)
    worksheet['A1'] = { 
      v: 'Campaign Master Data',
      s: { 
        font: { bold: true, color: { rgb: 'FFFFFF' } },
        fill: { fgColor: { rgb: '4472C4' } },
        alignment: { horizontal: 'center' }
      }
    };
    worksheet['E1'] = { 
      v: 'YouTube Performance',
      s: { 
        font: { bold: true, color: { rgb: 'FFFFFF' } },
        fill: { fgColor: { rgb: 'C5504B' } },
        alignment: { horizontal: 'center' }
      }
    };
    worksheet['I1'] = { 
      v: 'Instagram Performance',
      s: { 
        font: { bold: true, color: { rgb: 'FFFFFF' } },
        fill: { fgColor: { rgb: 'C55A11' } },
        alignment: { horizontal: 'center' }
      }
    };

    // Style the column headers row (second row)
    headers.forEach((header, index) => {
      const cellRef = XLSX.utils.encode_cell({ r: 1, c: index });
      worksheet[cellRef] = {
        v: header,
        s: {
          font: { bold: true },
          fill: { fgColor: { rgb: 'F2F2F2' } },
          alignment: { horizontal: 'center' }
        }
      };
    });

    // Add worksheet to workbook
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Campaign Affiliate Stats');

    // Generate Excel file and download
    XLSX.writeFile(workbook, `${filename}.xlsx`);

    return { success: true, message: 'Excel file exported successfully' };
  } catch (error) {
    console.error('Error exporting to Excel:', error);
    return { success: false, message: 'Failed to export Excel file', error: error.message };
  }
};

// Prepare data for export based on the current campaign and affiliate data
export const prepareExportData = (campaign, affiliates, companyName = '', brandName = '') => {
  try {
    if (!campaign || !affiliates || affiliates.length === 0) {
      return [];
    }

    const exportData = affiliates.map(affiliate => {
      // Get influencer name
      const influencerName = (affiliate.firstName && affiliate.firstName !== 'Unknown' && 
                             affiliate.lastName && affiliate.lastName !== 'User')
        ? `${affiliate.firstName} ${affiliate.lastName}`
        : affiliate.username || 'Unknown User';

      // Get YouTube statistics
      const youtubeStats = affiliate.youtubeStats || {};
      const youtubeVideos = youtubeStats.totalVideos || 0;
      const youtubeViews = youtubeStats.totalViews || 0;
      const youtubeLikes = youtubeStats.totalLikes || 0;
      const youtubeComments = youtubeStats.totalComments || 0;
      const youtubeEngagements = youtubeStats.totalEngagements || 0;

      // Get Instagram statistics
      const instagramStats = affiliate.instagramStats || {};
      const instagramReachs = instagramStats.totalReachs || 0;
      const instagramLikes = instagramStats.totalLikes || 0;
      const instagramComments = instagramStats.totalComments || 0;
      const instagramEngagements = instagramStats.totalEngagements || 0;

      return {
        companyName: companyName || 'N/A',
        brandName: brandName || campaign.brandName || 'N/A',
        campaignName: campaign.name || 'N/A',
        influencerName: influencerName,
        youtubeVideos,
        youtubeViews,
        youtubeLikes,
        youtubeComments,
        youtubeEngagements,
        instagramReachs,
        instagramLikes,
        instagramComments,
        instagramEngagements
      };
    });

    return exportData;
  } catch (error) {
    console.error('Error preparing export data:', error);
    return [];
  }
};

// Export summary statistics
export const exportSummaryStats = (summaryStats, campaign, filename = 'campaign-summary-stats') => {
  try {
    const summaryData = [
      ['Campaign Summary Statistics'],
      [''],
      ['Campaign Name', campaign?.name || 'N/A'],
      ['Campaign Type', campaign?.campaignType || 'N/A'],
      ['Total Influencers', summaryStats.totalAffiliates || 0],
      ['Total Visits', summaryStats.totalVisits || 0],
      ['Total Conversions', summaryStats.totalConversions || 0],
      ['Average Conversion Rate', `${summaryStats.avgConversionRate || 0}%`],
      [''],
      ['YouTube Performance'],
      ['Total Views', summaryStats.totalYouTubeViews || 0],
      ['Total Engagements', summaryStats.totalYouTubeEngagements || 0],
      [''],
      ['Instagram Performance'],
      ['Total Reachs', summaryStats.totalInstagramReachs || 0],
      ['Total Engagements', summaryStats.totalInstagramEngagements || 0]
    ];

    // Create workbook and worksheet
    const workbook = XLSX.utils.book_new();
    const worksheet = XLSX.utils.aoa_to_sheet(summaryData);

    // Set column widths
    worksheet['!cols'] = [{ wch: 25 }, { wch: 20 }];

    // Add worksheet to workbook
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Summary Statistics');

    // Generate Excel file and download
    XLSX.writeFile(workbook, `${filename}.xlsx`);

    return { success: true, message: 'Summary statistics exported successfully' };
  } catch (error) {
    console.error('Error exporting summary statistics:', error);
    return { success: false, message: 'Failed to export summary statistics', error: error.message };
  }
}; 

// Demo function to test export functionality
// export const testExport = () => {
//   const sampleData = [
//     {
//       companyName: 'Infuber Company',
//       brandName: 'Sample Brand',
//       campaignName: 'Test Campaign',
//       influencerName: 'John Doe',
//       youtubeVideos: 2,
//       youtubeViews: 1000,
//       youtubeLikes: 50,
//       youtubeComments: 10,
//       youtubeEngagements: 60,
//       instagramReachs: 5000,
//       instagramLikes: 200,
//       instagramComments: 15,
//       instagramEngagements: 230
//     },
//     {
//       companyName: 'Infuber Company',
//       brandName: 'Sample Brand',
//       campaignName: 'Test Campaign',
//       influencerName: 'Jane Smith',
//       youtubeVideos: 1,
//       youtubeViews: 500,
//       youtubeLikes: 25,
//       youtubeComments: 5,
//       youtubeEngagements: 30,
//       instagramReachs: 3000,
//       instagramLikes: 150,
//       instagramComments: 8,
//       instagramEngagements: 170
//     }
//   ];

//   console.log('Testing export functionality with section headers...');
//   console.log('Sample data:', sampleData);
//   console.log('Export format will include:');
//   console.log('Row 1: Campaign Master Data | YouTube Performance | Instagram Performance');
//   console.log('Row 2: Company Name | Brand Name | Campaign Name | Influencer Name | Videos | Views | Likes | Comments | Engagements | Reachs | Likes | Comments | Engagements');
//   console.log('Row 3+: Data rows...');
  
//   // Test CSV export
//   const csvResult = exportToCSV(sampleData, 'test-export-with-headers');
//   console.log('CSV export result:', csvResult);
  
//   // Test Excel export
//   const excelResult = exportToExcel(sampleData, 'test-export-with-headers');
//   console.log('Excel export result:', excelResult);
  
//   return { csvResult, excelResult };
// }; 

// Preview function to show export format
// export const previewExportFormat = (data) => {
//   try {
//     if (!data || data.length === 0) {
//       return 'No data available for preview';
//     }

//     const headers = [
//       'Company Name',
//       'Brand Name', 
//       'Campaign Name',
//       'Influencer Name (Affiliate Name)',
//       'Videos',
//       'Views',
//       'Likes', 
//       'Comments',
//       'Engagements',
//       'Reachs',
//       'Likes',
//       'Comments',
//       'Engagements'
//     ];

//     let preview = 'EXPORT PREVIEW:\n';
//     preview += '='.repeat(80) + '\n\n';
    
//     // Section headers row
//     preview += 'Row 1: Campaign Master Data | YouTube Performance | Instagram Performance\n';
//     preview += 'Row 2: ' + headers.join(' | ') + '\n';
//     preview += 'Row 3+: Data rows...\n\n';
    
//     // Show first few data rows
//     preview += 'Sample Data Rows:\n';
//     preview += '-'.repeat(80) + '\n';
    
//     data.slice(0, 3).forEach((row, index) => {
//       preview += `Row ${index + 3}: ${row.companyName} | ${row.brandName} | ${row.campaignName} | ${row.influencerName} | ${row.youtubeVideos} | ${row.youtubeViews} | ${row.youtubeLikes} | ${row.youtubeComments} | ${row.youtubeEngagements} | ${row.instagramReachs} | ${row.instagramLikes} | ${row.instagramComments} | ${row.instagramEngagements}\n`;
//     });
    
//     if (data.length > 3) {
//       preview += `... and ${data.length - 3} more rows\n`;
//     }
    
//     preview += '\n' + '='.repeat(80);
    
//     return preview;
//   } catch (error) {
//     console.error('Error generating preview:', error);
//     return 'Error generating preview';
//   }
// }; 